<template>
  <div class="container-fluid py-4">
    <!-- Header -->
    <div class="row mb-4">
      <div class="col-12 d-flex align-items-center justify-content-between">
        <div class="d-flex align-items-center">
          <div class="bg-primary-light p-3 rounded-2 me-3">
            <vue-feather type="message-square" stroke="#652aa3" size="24" />
          </div>
          <h3 class="mb-0 fw-bold">My Feedback</h3>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <!-- Manager Reviews assigned to me -->
        <Card cardClasses="shadow-sm border-0 h-100 mb-4">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <div class="font-size-125 color-gray-600">
                <vue-feather type="check-square" stroke="#652aa3" size="18" class="me-1" />
                Manager Reviews Assigned to Me ({{ filteredManagerReviews.length }})
              </div>
              <UInput v-model="managerSearch" size="md" placeholder="Search reviews" class="mb-0 w-25 search-input" />
            </div>

            <div v-if="loadingManagers" class="text-center py-4">
              <Spinner spinner-class="mb-1" />
            </div>

            <div v-else-if="filteredManagerReviews.length > 0" class="table-responsive">
              <table class="table table-hover align-middle">
                <thead class="table-light">
                  <tr>
                    <th>Employee</th>
                    <th>Cycle</th>
                    <th>Status</th>
                    <th>Updated</th>
                    <th class="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="review in filteredManagerReviews" :key="review.id">
                    <td>
                      <div class="d-flex align-items-center">
                        <div class="avatar avatar-sm me-2">
                          <span class="avatar-title rounded-circle bg-info-subtle">
                            {{ getInitials(review.employee?.name || (review.employee?.first_name + ' ' + review.employee?.last_name)) }}
                          </span>
                        </div>
                        <div>
                          <div class="fw-semibold">{{ review.employee?.name || (review.employee?.first_name + ' ' + review.employee?.last_name) }}</div>
                          <small class="text-muted">{{ review.employee?.department?.name }}</small>
                        </div>
                      </div>
                    </td>
                    <td>{{ review.reviewCycle?.name || review.review_cycle?.name }}</td>
                    <td>
                      <span :class="getStatusBadgeClass(review.status)">
                        {{ formatStatus(review.status) }}
                      </span>
                    </td>
                    <td>{{ formatDate(review.updated_at) }}</td>
                    <td class="text-end">
                      <button
                        v-if="canSubmitManager(review)"
                        class="btn btn-sm btn-primary"
                        @click="openManagerModal(review)"
                      >
                        <i class="icon-feather-edit-2 me-1"></i>Review
                      </button>
                      <router-link
                        :to="`/performance/review/${review.id}`"
                        class="btn btn-sm btn-outline-primary ms-2"
                      >
                        <i class="icon-feather-eye me-1"></i>View
                      </router-link>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-else class="text-center py-5 text-muted">
              <i class="icon-feather-clipboard mb-2" style="font-size: 2rem;"></i>
              <p>No manager reviews assigned to you</p>
            </div>
          </div>
        </Card>

        <!-- 360 Feedback requests assigned to me -->
        <Card cardClasses="shadow-sm border-0 h-100 mb-4">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <div class="font-size-125 color-gray-600">
                <vue-feather type="users" stroke="#652aa3" size="18" class="me-1" />
                360 Feedback Requests ({{ filteredFeedbackRequests.length }})
              </div>
              <UInput v-model="feedbackSearch" size="md" placeholder="Search feedback" class="mb-0 w-25 search-input" />
            </div>

            <div v-if="loadingFeedback" class="text-center py-4">
              <Spinner spinner-class="mb-1" />
            </div>

            <div v-else-if="filteredFeedbackRequests.length > 0" class="table-responsive">
              <table class="table table-hover align-middle">
                <thead class="table-light">
                  <tr>
                    <th>Employee</th>
                    <th>Cycle</th>
                    <th>Requested By</th>
                    <th>Status</th>
                    <th class="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="req in filteredFeedbackRequests" :key="req.id">
                    <td>
                      <div class="fw-semibold">
                        {{ req.review?.employee?.full_name || ((req.review?.employee?.first_name || '') + ' ' + (req.review?.employee?.last_name || '')) }}
                      </div>
                    </td>
                    <td>{{ req.review?.review_cycle?.name || '-' }}</td>
                    <td>
                      {{ req.requested_by?.full_name || ((req.requested_by?.first_name || '') + ' ' + (req.requested_by?.last_name || '')) }}
                    </td>
                    <td>
                      <span :class="getFeedbackStatusBadge(req.status)">{{ formatFeedbackStatus(req.status) }}</span>
                    </td>
                    <td class="text-end">
                      <router-link
                        v-if="req.status === 'pending'"
                        :to="`/performance/feedback/${req.id}`"
                        class="btn btn-sm btn-primary"
                      >
                        <i class="icon-feather-edit me-1"></i>Provide Feedback
                      </router-link>
                      <button v-else class="btn btn-sm btn-outline-secondary" disabled>
                        <i class="icon-feather-edit me-1"></i>Provide Feedback
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-else class="text-center py-5 text-muted">
              <i class="icon-feather-users mb-2" style="font-size: 2rem;"></i>
              <p>No feedback requests</p>
            </div>
          </div>
        </Card>
      </div>
    </div>

    <!-- Manager Review Modal -->
    <div v-if="showManagerModal">
      <div class="modal fade show d-block" tabindex="-1" @click.self="closeManagerModal">
        <div class="modal-dialog modal-xl">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Submit Manager Review</h5>
              <button type="button" class="btn-close" @click="closeManagerModal"></button>
            </div>
            <div class="modal-body">
              <!-- Step indicator -->
              <div class="d-flex align-items-center mb-3">
                <div :class="['me-2', managerModalStep === 1 ? 'fw-bold text-primary' : 'text-muted']">
                  1. Self Review Scores
                </div>
                <i class="bi bi-chevron-right mx-2"></i>
                <div :class="[managerModalStep === 2 ? 'fw-bold text-primary' : 'text-muted']">
                  2. Manager Review
                </div>
              </div>

              <!-- Step 1: Self review responses and scoring -->
              <SelfReviewResponses
                v-if="selectedReview && managerModalStep === 1"
                :review="selectedReview"
                @scores-submitted="handleScoresSubmitted"
              />

              <!-- Step 2: Manager review form -->
              <ManagerReviewForm
                v-if="selectedReview && managerModalStep === 2"
                :review="selectedReview"
                :canSubmit="true"
                @submitted="handleManagerSubmit"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="modal-backdrop fade show"></div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import performanceService from '@/api/performanceService';
import feedbackRequestService from '@/api/feedbackRequestService';
import ManagerReviewForm from '@/components/performance/ManagerReviewForm.vue';
import SelfReviewResponses from '@/components/performance/SelfReviewResponses.vue';
import VueFeather from 'vue-feather';
import { usePerformanceStore } from '@/stores/performance';
import {decrypt} from "@/utils/crypto";

export default {
  name: 'MyFeedback',
  components: { ManagerReviewForm, SelfReviewResponses, VueFeather },
  data() {
    return {
      // Lists
      managerReviews: [],
      feedbackRequests: [],
      // Loading
      loadingManagers: false,
      loadingFeedback: false,
      // UI state
      managerSearch: '',
      feedbackSearch: '',
      showManagerModal: false,
      selectedReview: null,
      submitting: false,
      managerModalStep: 1,
    };
  },
  computed: {
    userId() {
      let profile = JSON.parse(decrypt(sessionStorage.getItem('profile')))
      return profile.id
    },
    filteredManagerReviews() {
      const q = this.managerSearch.trim().toLowerCase();
      if (!q) return this.managerReviews;
      return this.managerReviews.filter(r => {
        const emp = r.employee?.name || `${r.employee?.first_name || ''} ${r.employee?.last_name || ''}`;
        const cycle = r.reviewCycle?.name || r.review_cycle?.name || '';
        return (
          (emp || '').toLowerCase().includes(q) ||
          (cycle || '').toLowerCase().includes(q) ||
          (r.status || '').toLowerCase().includes(q)
        );
      });
    },
    filteredFeedbackRequests() {
      const q = this.feedbackSearch.trim().toLowerCase();
      if (!q) return this.feedbackRequests;
      return this.feedbackRequests.filter(req => {
        const employeeName = (req.review?.employee?.full_name || `${req.review?.employee?.first_name || ''} ${req.review?.employee?.last_name || ''}`).trim().toLowerCase();
        const cycleName = (req.review?.review_cycle?.name || '').toLowerCase();
        const requestedBy = (req.requested_by?.full_name || `${req.requested_by?.first_name || ''} ${req.requested_by?.last_name || ''}`).trim().toLowerCase();
        const status = (req.status || '').toLowerCase();
        return employeeName.includes(q) || cycleName.includes(q) || requestedBy.includes(q) || status.includes(q);
      });
    }
  },
  methods: {
    async fetchManagerReviews() {
      try {
        this.loadingManagers = true;
        const res = await performanceService.getManagerReviews(this.userId);
        this.managerReviews = res?.data || res || {};
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to load manager reviews', e);
      } finally {
        this.loadingManagers = false;
      }
    },
    async fetchFeedbackRequests() {
      try {
        this.loadingFeedback = true;
        const res = await feedbackRequestService.getUserFeedbackRequests(this.userId);
        this.feedbackRequests = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to load feedback requests', e);
      } finally {
        this.loadingFeedback = false;
      }
    },
    async openManagerModal(review) {
      try {
        // Load full review detail to populate ManagerReviewForm
        const res = await performanceService.getReview(review.id);
        const payload = res?.data || res || {};
        this.selectedReview = payload;
        // Determine initial step: if there are pending unscored responses for reviewer, start at Step 1
        const pending = Array.isArray(this.selectedReview?.responses)
          ? this.selectedReview.responses.filter(r => r.score == null).length
          : 0;
        const isReviewer = String(this.selectedReview?.reviewer_id) === String(this.userId);
        this.managerModalStep = (isReviewer && pending > 0) ? 1 : 2;
        this.showManagerModal = true;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to load review details', e);
      }
    },
    closeManagerModal() {
      this.showManagerModal = false;
      this.selectedReview = null;
      this.managerModalStep = 1;
    },
    async handleManagerSubmit(payload) {
      try {
        this.submitting = true;
        const performanceStore = usePerformanceStore();
        await performanceStore.submitManagerReview(this.selectedReview.id, payload);
        this.closeManagerModal();
        await this.fetchManagerReviews();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to submit manager review', e);
      } finally {
        this.submitting = false;
      }
    },
    handleScoresSubmitted(updatedReview) {
      // Update the selected review with new scores and move to Step 2
      this.selectedReview = updatedReview || this.selectedReview;
      this.managerModalStep = 2;
    },
    getInitials(name) {
      if (!name) return '';
      return name.split(' ').map(n => n[0]).join('').toUpperCase();
    },
    formatDate(date) {
      if (!date) return 'N/A';
      return moment(date).format('MMM DD, YYYY');
    },
    formatStatus(status) {
      const map = { pending: 'Pending', self_review: 'Self Review', manager_review: 'Manager Review', calibration: 'Calibration', completed: 'Completed' };
      return map[status] || status;
    },
    getStatusBadgeClass(status) {
      const classes = { pending: 'badge bg-secondary', self_review: 'badge bg-warning', manager_review: 'badge bg-info', calibration: 'badge bg-primary', completed: 'badge bg-success' };
      return classes[status] || 'badge bg-secondary';
    },
    formatFeedbackStatus(s) {
      const map = { pending: 'Pending', submitted: 'Submitted', completed: 'Submitted', declined: 'Declined' };
      return map[s] || s;
    },
    getFeedbackStatusBadge(s) {
      const classes = { pending: 'badge bg-warning', submitted: 'badge bg-success', completed: 'badge bg-success', declined: 'badge bg-danger' };
      return classes[s] || 'badge bg-secondary';
    },
    canSubmitManager(review) {
      const statusOk = ['manager_review', 'calibration'].includes(review.status);
      return statusOk && String(review.reviewer_id) === String(this.userId);
    }
  },
  mounted() {
    Promise.all([this.fetchManagerReviews(), this.fetchFeedbackRequests()]).catch(err => console.error(err));
  }
};
</script>

<style scoped>
.avatar {
  width: 32px;
  height: 32px;
}
.avatar-title {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-size: 0.875rem;
  font-weight: 600;
}
</style>
